import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import VideoCard from './VideoCard';
import React from 'react';

// Default fallback background - dark cinematic gradient
const DEFAULT_CHAPTER_BG = 'linear-gradient(to bottom, #0a0a0a 0%, #1a1512 50%, #0a0a0a 100%)';

const ChapterSection = ({ chapters, linkTitles = true }) => {
  // Helper to get proper video URL
  const getVideoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // For static mode, URLs are already absolute paths like `/media/...`
    if (url.startsWith('/')) return url;
    return url;
  };

  // Filter chapters that have at least one video with a valid URL
  const chaptersWithContent = chapters.filter(chapter => {
    const validVideos = chapter.videos?.filter(video => 
      video.youtube_url && video.youtube_url.trim() !== ''
    ) || [];
    return validVideos.length > 0;
  });

  // If no chapters have content, don't render the section
  if (chaptersWithContent.length === 0) {
    return null;
  }

  return (
    <section
      id="chapters"
      className="relative"
      data-testid="chapter-section"
    >
      {/* Render each chapter as its own section with independent background */}
      {chaptersWithContent.map((chapter) => {
        // Filter videos with valid URLs for this chapter
        const validVideos = chapter.videos?.filter(video => 
          video.youtube_url && video.youtube_url.trim() !== ''
        ) || [];

        const chapterBgVideo = getVideoUrl(chapter.background_video_url);

        return (
          <div
            key={chapter.chapter_number}
            className="relative min-h-screen py-32 px-6 md:px-12 lg:px-24 overflow-hidden"
            data-testid={`chapter-${chapter.chapter_number}`}
          >
            {/* Chapter-specific background video OR fallback gradient */}
            {chapterBgVideo ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: 0.7, zIndex: 0 }}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src={chapterBgVideo} type={chapterBgVideo.endsWith('.mov') ? 'video/quicktime' : chapterBgVideo.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
              </video>
            ) : (
              <div 
                className="absolute inset-0"
                style={{ background: DEFAULT_CHAPTER_BG }}
              />
            )}
            
            {/* Dark overlay for text readability */}
            <div
              className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60"
              style={{ zIndex: 1 }}
            />
            
            {/* Chapter content */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
              className="relative z-10 max-w-7xl mx-auto space-y-24"
            >
              {/* Chapter Header */}
              <div className="text-center mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="text-6xl md:text-7xl lg:text-8xl font-cinzel text-transparent bg-clip-text bg-gradient-to-b from-[#E6C86E] via-[#C0A062] to-[#9F7928] mb-6"
                  style={{ 
                    textShadow: '0 0 30px rgba(192, 160, 98, 0.4), 0 4px 8px rgba(0, 0, 0, 0.5)',
                    letterSpacing: '0.05em'
                  }}
                >
                  {linkTitles ? (
                    <Link
                      to={`/app/chapter/${chapter.chapter_number}`}
                      className="bg-gradient-to-b from-[#E6C86E] via-[#C0A062] to-[#9F7928] bg-clip-text text-transparent transition-opacity duration-300 hover:opacity-80"
                    >
                      Chapter {chapter.chapter_number}
                    </Link>
                  ) : (
                    `Chapter ${chapter.chapter_number}`
                  )}
                </motion.div>
                {chapter.title && (
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    viewport={{ once: true }}
                    className="text-3xl md:text-4xl lg:text-5xl font-cinzel text-white mb-4"
                    style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.8)' }}
                    data-testid={`chapter-title-${chapter.chapter_number}`}
                  >
                    {linkTitles ? (
                      <Link
                        to={`/app/chapter/${chapter.chapter_number}`}
                        className="transition-colors duration-300 hover:text-[#E6C86E]"
                      >
                        {chapter.title}
                      </Link>
                    ) : (
                      chapter.title
                    )}
                  </motion.h2>
                )}
                {chapter.description && (
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto font-manrope"
                    style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)' }}
                  >
                    {chapter.description}
                  </motion.p>
                )}
              </div>

              {/* Video Cards - Only render videos with valid URLs */}
              {validVideos.map((video, videoIndex) => {
                const isEven = videoIndex % 2 === 0;
                return (
                  <motion.div
                    key={video.position || videoIndex}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: videoIndex * 0.15 }}
                    viewport={{ once: true, margin: "-50px" }}
                    className="space-y-8"
                    data-testid={`video-section-${videoIndex}`}
                  >
                    {/* Video Card with spacing below */}
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                      isEven ? '' : 'lg:flex-row-reverse'
                    }`}>
                      {isEven ? (
                        <>
                          {/* Video embed */}
                          <div className="mb-8 lg:mb-0">
                            <VideoCard videoUrl={video.youtube_url} />
                          </div>
                          
                          {/* Text content with improved styling */}
                          <div className="space-y-5 relative p-6 md:p-8 border-l-2 border-gold/30">
                            {/* Video Title with dark brown background panel */}
                            {video.title && (
                              <div 
                                className="inline-block px-4 py-2 rounded-sm"
                                style={{ 
                                  backgroundColor: 'rgba(45, 30, 20, 0.9)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                                }}
                              >
                                <h3 
                                  className="text-xl md:text-2xl font-cinzel"
                                  style={{ 
                                    color: '#E6C86E',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                                  }}
                                >
                                  {video.title}
                                </h3>
                              </div>
                            )}
                            {/* Description with improved readability */}
                            {video.text && (
                              <p 
                                className="text-base md:text-lg lg:text-xl text-gray-200 leading-relaxed font-manrope font-light"
                                style={{ 
                                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 4px 16px rgba(0, 0, 0, 0.5)'
                                }}
                              >
                                {video.text}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Text content (left side on odd items) */}
                          <div className="space-y-5 relative p-6 md:p-8 border-l-2 border-gold/30 lg:order-1">
                            {/* Video Title with dark brown background panel */}
                            {video.title && (
                              <div 
                                className="inline-block px-4 py-2 rounded-sm"
                                style={{ 
                                  backgroundColor: 'rgba(45, 30, 20, 0.9)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                                }}
                              >
                                <h3 
                                  className="text-xl md:text-2xl font-cinzel"
                                  style={{ 
                                    color: '#E6C86E',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                                  }}
                                >
                                  {video.title}
                                </h3>
                              </div>
                            )}
                            {/* Description with improved readability */}
                            {video.text && (
                              <p 
                                className="text-base md:text-lg lg:text-xl text-gray-200 leading-relaxed font-manrope font-light"
                                style={{ 
                                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 4px 16px rgba(0, 0, 0, 0.5)'
                                }}
                              >
                                {video.text}
                              </p>
                            )}
                          </div>
                          
                          {/* Video embed (right side on odd items) */}
                          <div className="mb-8 lg:mb-0 lg:order-2">
                            <VideoCard videoUrl={video.youtube_url} />
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        );
      })}
    </section>
  );
};

export default ChapterSection;
